import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { projectPulseApi } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Activity, TrendingUp, TrendingDown, Clock, DollarSign, ChevronRight,
} from "lucide-react";
import { cn } from "../lib/utils";

const Profitability = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("hours");

  useEffect(() => {
    projectPulseApi.getAll().then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div>
      </div>
    );
  }

  const projects = data?.projects || [];

  const totals = projects.reduce((acc, p) => ({
    hours: acc.hours + p.hours,
    revenue: acc.revenue + p.revenue,
    cost: acc.cost + p.cost,
    profit: acc.profit + p.profit,
  }), { hours: 0, revenue: 0, cost: 0, profit: 0 });

  const totalMargin = totals.revenue > 0 ? ((totals.profit / totals.revenue) * 100).toFixed(1) : 0;

  const sorted = [...projects].sort((a, b) => {
    if (sortBy === "hours") return b.hours - a.hours;
    if (sortBy === "profit") return b.profit - a.profit;
    if (sortBy === "margin") return b.margin - a.margin;
    if (sortBy === "revenue") return b.revenue - a.revenue;
    return 0;
  });

  const getMarginColor = (margin) => {
    if (margin >= 40) return "text-emerald-400";
    if (margin >= 20) return "text-[#6dbf4a]";
    if (margin >= 0) return "text-amber-400";
    return "text-red-400";
  };

  const getMarginBg = (margin) => {
    if (margin >= 40) return "bg-emerald-500/15 border-emerald-500/30";
    if (margin >= 20) return "bg-[#6dbf4a]/10 border-[#6dbf4a]/30";
    if (margin >= 0) return "bg-amber-500/15 border-amber-500/30";
    return "bg-red-500/15 border-red-500/30";
  };

  return (
    <div className="space-y-6 slide-up" data-testid="profitability-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-5 h-5 text-[#6dbf4a]" />
          <h1 className="text-2xl font-bold tracking-tight text-white">Lonsamhet</h1>
        </div>
        <p className="text-white/25 text-sm">Oversikt over alla projekt</p>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="card-surface overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-white/20" />
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wider text-white/25">Projekt</p>
            <p className="stat-number text-xl text-white mt-1">{projects.length}</p>
          </CardContent>
        </Card>
        <Card className="card-surface overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-[#6dbf4a]" />
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wider text-white/25">Timmar</p>
            <p className="stat-number text-xl text-white mt-1">{totals.hours.toFixed(0)}h</p>
          </CardContent>
        </Card>
        <Card className="card-surface overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-[#6dbf4a]" />
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wider text-white/25">Intakt</p>
            <p className="stat-number text-xl text-white mt-1">{(totals.revenue / 1000).toFixed(0)}k</p>
          </CardContent>
        </Card>
        <Card className="card-surface overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-red-500" />
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wider text-white/25">Kostnad</p>
            <p className="stat-number text-xl text-red-400 mt-1">{(totals.cost / 1000).toFixed(0)}k</p>
          </CardContent>
        </Card>
        <Card className="card-surface overflow-hidden relative">
          <div className={`absolute top-0 left-0 w-full h-0.5 ${totals.profit >= 0 ? 'bg-emerald-500' : 'bg-orange-500'}`} />
          <CardContent className="pt-4 pb-3">
            <p className="text-[10px] uppercase tracking-wider text-white/25">Vinst</p>
            <p className={`stat-number text-xl mt-1 flex items-center gap-1 ${totals.profit >= 0 ? "text-emerald-400" : "text-orange-400"}`}>
              {totals.profit >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {(totals.profit / 1000).toFixed(0)}k
              <span className="text-xs opacity-60 ml-1">({totalMargin}%)</span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Sort buttons */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
        {[
          { key: "hours", label: "Timmar" },
          { key: "revenue", label: "Intakt" },
          { key: "profit", label: "Vinst" },
          { key: "margin", label: "Marginal" },
        ].map(s => (
          <button key={s.key} onClick={() => setSortBy(s.key)}
            className={cn(
              "px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
              sortBy === s.key ? "bg-[#6dbf4a]/15 text-[#6dbf4a]" : "text-white/30 hover:text-white/60"
            )}
            data-testid={`sort-${s.key}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Project grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map(p => {
          const maxRevenue = Math.max(...projects.map(x => x.revenue), 1);
          const revPct = (p.revenue / maxRevenue) * 100;
          const costPct = (p.cost / maxRevenue) * 100;
          const budgetPct = p.budget_hours > 0 ? Math.round((p.hours / p.budget_hours) * 100) : null;

          return (
            <Card key={p.project_id}
              className="bg-[#0d1218] border-white/[0.06] hover:border-[#6dbf4a]/40 cursor-pointer transition-all group"
              onClick={() => navigate(`/projects/${p.project_id}`)}
              data-testid={`profit-project-${p.project_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-white truncate">{p.name}</h3>
                    {p.client_name && <p className="text-[11px] text-white/30 truncate">{p.client_name}</p>}
                  </div>
                  <Badge className={cn("text-xs font-bold border", getMarginBg(p.margin), getMarginColor(p.margin))} variant="outline">
                    {p.margin}%
                  </Badge>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div>
                    <p className="text-[10px] text-white/20">Timmar</p>
                    <p className="stat-number text-sm text-white">{p.hours.toFixed(0)}h</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/20">Intakt</p>
                    <p className="stat-number text-sm text-white">{p.revenue > 0 ? `${(p.revenue / 1000).toFixed(1)}k` : '0'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/20">Vinst</p>
                    <p className={cn("stat-number text-sm", p.profit >= 0 ? "text-emerald-400" : "text-orange-400")}>
                      {p.profit >= 0 ? "+" : ""}{p.profit > 1000 || p.profit < -1000 ? `${(p.profit / 1000).toFixed(1)}k` : p.profit.toLocaleString("sv-SE")}
                    </p>
                  </div>
                </div>

                {/* Revenue/Cost bar */}
                <div className="relative h-2 bg-white/[0.04] rounded-full overflow-hidden mb-2">
                  <div className="absolute inset-y-0 left-0 bg-[#6dbf4a]/50 rounded-full" style={{ width: `${Math.max(revPct, 1)}%` }} />
                  <div className="absolute inset-y-0 left-0 bg-red-500/40 rounded-full" style={{ width: `${costPct}%` }} />
                </div>

                {/* Budget progress */}
                {budgetPct !== null && (
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-white/20">Budget</span>
                    <span className={cn(
                      budgetPct > 90 ? "text-red-400" : budgetPct > 70 ? "text-amber-400" : "text-white/30"
                    )}>{budgetPct}% ({p.hours.toFixed(0)}/{p.budget_hours}h)</span>
                  </div>
                )}

                <div className="flex items-center justify-end mt-2 text-[11px] text-white/20 group-hover:text-[#6dbf4a] transition-colors">
                  Detaljer <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-16">
          <Activity className="w-10 h-10 mx-auto mb-3 text-gray-700" />
          <p className="text-white/40 text-sm">Inga aktiva projekt med tidsposter</p>
        </div>
      )}
    </div>
  );
};

export default Profitability;
